/**
 * The project configuration editor: structured controls over the one document Nightshift
 * stores for each project.
 *
 * The General, Execution and Pipeline categories each show some of its sections; all three
 * load the same document and save it whole, naming the revision they loaded. A save that
 * finds a newer revision is refused by the server and nothing is merged: the editor stops
 * saving and offers a reload, so nobody's change disappears silently. Members see the same
 * values with every control disabled.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import {
  CAPABILITIES,
  CAPABILITY_HELP,
  DISCIPLINES,
  ENGINE_FAMILIES,
  ENVIRONMENT_HOSTS,
  ENVIRONMENT_OPERATIONS,
  OBSOLETE_TICKET_POLICIES,
  PROFILE_ARRANGEMENTS,
  PROJECT_PHASES,
  PROJECT_PROFILES,
  REVIEW_FINDINGS_SHOWN,
  WORK_CATEGORIES,
  WORK_ITEM_TYPES,
  producerConfigDigest,
  type ArtifactsSection,
  type EnvironmentSection,
  type ProjectConfiguration,
  type ReviewFindingsShown,
  type VerificationProducer,
  type VerificationSection,
} from '@nightshift/config';
import type { UiProjectConfiguration } from '@nightshift/ui-types';

import { Empty, Kicker, Spinner } from '../components/primitives.tsx';
import { ApiError, api, type CatalogueModel, type SessionUser } from '../live/api.ts';
import { canWriteSettings } from '../lib/privilege.ts';
import { useSettingsAutosave, type AutosaveState } from '../lib/settings-autosave.tsx';
import { ExecutionRoutesEditor, type EditorMachine } from './execution-routes-editor.tsx';

export type ConfigurationSection =
  | 'identity'
  | 'profile'
  | 'execution'
  | 'tags'
  | 'environment'
  | 'commands'
  | 'review'
  | 'refine'
  | 'guidance'
  | 'verification'
  | 'artifacts';

const STAGES = ['refine', 'plan', 'implement', 'test', 'review'] as const;
const GUIDANCE_STAGES = ['refine', 'plan', 'implement', 'review'] as const;
const SHELLS = ['', 'pwsh', 'powershell', 'bash', 'sh', 'zsh', 'cmd'] as const;

export function ProjectConfigurationPanel({
  projectSlug,
  user,
  sections,
}: {
  projectSlug: string | null;
  user: SessionUser | null;
  sections: readonly ConfigurationSection[];
}) {
  const [snapshot, setSnapshot] = useState<UiProjectConfiguration | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Bumped on every load so a reload replaces the form outright, discarding an unsaved draft.
  const [generation, setGeneration] = useState(0);
  const [machines, setMachines] = useState<EditorMachine[]>([]);
  const [models, setModels] = useState<CatalogueModel[]>([]);
  const wantsRoutes = sections.includes('execution');

  const load = useCallback(async () => {
    if (!projectSlug) return;
    try {
      const next = await api.projectConfiguration(projectSlug);
      setSnapshot(next);
      setGeneration((value) => value + 1);
      setLoadError(null);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : String(error));
    }
  }, [projectSlug]);

  useEffect(() => {
    setSnapshot(null);
    setLoadError(null);
    void load();
  }, [load]);

  // The route editor's dropdowns come from the machine registry and the live model catalogue.
  // A failure here must not block editing, so both fall back to empty and offer fewer choices.
  useEffect(() => {
    if (!wantsRoutes) return;
    let cancelled = false;
    void (async () => {
      const [machineList, catalogue] = await Promise.all([
        api.machines().catch(() => null),
        api.modelsCatalogue(projectSlug).catch(() => null),
      ]);
      if (cancelled) return;
      setMachines(
        (machineList?.items ?? []).map((item) => ({
          name: item.name,
          installedHarnesses: item.installedHarnesses,
          live: item.live,
          paused: item.paused,
        })),
      );
      setModels(catalogue?.models ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, [projectSlug, wantsRoutes]);

  if (!projectSlug) {
    return <Empty title="Select a project" body="Project configuration belongs to one project. Pick one in the header to see or edit it." />;
  }
  if (!snapshot && !loadError) return <Spinner size={20} />;
  if (!snapshot) {
    return (
      <Empty
        title="Project configuration could not be read"
        body={loadError ?? ''}
        action={<button className="btn" type="button" onClick={() => void load()}>Try again</button>}
      />
    );
  }
  return (
    <ConfigurationForm
      key={generation}
      projectSlug={projectSlug}
      snapshot={snapshot}
      writable={snapshot.canEdit && canWriteSettings(user)}
      sections={sections}
      machines={machines}
      models={models}
      onReload={() => void load()}
    />
  );
}

function ConfigurationForm({
  projectSlug,
  snapshot,
  writable,
  sections,
  machines,
  models,
  onReload,
}: {
  projectSlug: string;
  snapshot: UiProjectConfiguration;
  writable: boolean;
  sections: readonly ConfigurationSection[];
  machines: EditorMachine[];
  models: CatalogueModel[];
  onReload: () => void;
}) {
  const revisionRef = useRef(snapshot.revision);
  const [revision, setRevision] = useState(snapshot.revision);
  const [savedBy, setSavedBy] = useState({ at: snapshot.updatedAt, by: snapshot.updatedBy });
  const [conflict, setConflict] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);

  const save = useCallback(
    async (next: ProjectConfiguration) => {
      try {
        const result = await api.saveProjectConfiguration(projectSlug, next, revisionRef.current);
        revisionRef.current = result.revision;
        setRevision(result.revision);
        setSavedBy({ at: result.updatedAt, by: result.updatedBy });
        setWarnings(result.warnings);
      } catch (error) {
        if (error instanceof ApiError && error.status === 409) {
          setConflict(true);
          throw new Error('Someone else saved this project configuration after you opened it. Reload to see their version; this edit was not saved.', { cause: error });
        }
        const errors = error instanceof ApiError ? error.body?.errors : undefined;
        if (Array.isArray(errors) && errors.length > 0) throw new Error(errors.join(' · '), { cause: error });
        throw error;
      }
    },
    [projectSlug],
  );

  const autosave = useSettingsAutosave<ProjectConfiguration>({
    value: snapshot.configuration,
    save,
    what: 'Project configuration',
    enabled: writable && !conflict,
    delay: 800,
    validate: incompleteRows,
  });
  const { draft, setDraft, status, error } = autosave;
  const disabled = !writable || conflict;
  const update = (change: (current: ProjectConfiguration) => ProjectConfiguration) => setDraft((current) => change(current));
  const defaults = snapshot.defaults;

  return (
    <div className="cfg-stack" data-configuration-revision={revision}>
      {snapshot.status !== 'ready' ? (
        <div className="cfg-error cfg-alert" role="alert" data-configuration-status={snapshot.status}>
          {snapshot.status === 'import-pending'
            ? 'This project’s existing configuration has not been imported yet, so its work does not dispatch. The values below are the built-in defaults.'
            : `This project’s existing configuration could not be imported${snapshot.issues.length > 0 ? `: ${snapshot.issues.join('; ')}` : ''}. Its work does not dispatch until the configuration is saved here.`}
        </div>
      ) : null}
      <ConfigurationStatus revision={revision} savedBy={savedBy} conflict={conflict} writable={writable} status={status} />
      {conflict ? (
        <div className="cfg-error cfg-alert" role="alert" data-configuration-conflict>
          {error}{' '}
          <button type="button" className="btn btn-sm" onClick={onReload}>Reload</button>
        </div>
      ) : error ? (
        <div className="cfg-error cfg-alert" role="alert">Unsaved: {error}</div>
      ) : null}
      {warnings.length > 0 ? <div className="cfg-help">Saved with notes: {warnings.join(' · ')}</div> : null}

      {sections.includes('identity') ? <IdentitySection draft={draft} update={update} disabled={disabled} /> : null}
      {sections.includes('profile') ? <ProfileSection draft={draft} update={update} disabled={disabled} /> : null}
      {sections.includes('execution') ? (
        <ExecutionRoutesEditor
          execution={draft.execution}
          onChange={(execution) => update((current) => ({ ...current, execution }))}
          machines={machines}
          models={models}
          disabled={disabled}
        />
      ) : null}
      {sections.includes('execution') ? (
        <Panel title="Implementer report">
          <Row label="Structured implementer report" path="execution.implementer_report_version" help="Asks implementers for the version 1 report. Turn on only once every runner that implements for this project supports it.">
            <Toggle
              label="Structured implementer report"
              value={draft.execution.implementer_report_version === 1}
              disabled={disabled}
              onChange={(on) =>
                update((current) => {
                  const execution = { ...current.execution };
                  if (on) execution.implementer_report_version = 1;
                  else delete execution.implementer_report_version;
                  return { ...current, execution };
                })
              }
            />
          </Row>
        </Panel>
      ) : null}
      {sections.includes('tags') ? <TagsSection draft={draft} update={update} disabled={disabled} /> : null}
      {sections.includes('environment') ? <EnvironmentEditor draft={draft} update={update} disabled={disabled} /> : null}
      {sections.includes('commands') ? <CommandsSection draft={draft} update={update} disabled={disabled} /> : null}
      {sections.includes('review') ? <ReviewSection draft={draft} update={update} disabled={disabled} defaults={defaults} /> : null}
      {sections.includes('refine') ? <RefineSection draft={draft} update={update} disabled={disabled} /> : null}
      {sections.includes('guidance') ? <GuidanceSection draft={draft} update={update} disabled={disabled} /> : null}
      {sections.includes('verification') ? <VerificationEditor draft={draft} update={update} disabled={disabled} /> : null}
      {sections.includes('artifacts') ? <ArtifactsEditor draft={draft} update={update} disabled={disabled} /> : null}
    </div>
  );
}

/** Rows still being filled in: saving them would only earn a validation error per keystroke. */
function incompleteRows(configuration: ProjectConfiguration): string | null {
  for (const [name, producer] of Object.entries(configuration.verification?.producers ?? {})) {
    if (!name.trim() || !producer.job.trim() || !producer.result.trim() || producer.provides.length === 0) {
      return 'Each verification producer needs a name, a job, what it provides and a result path.';
    }
  }
  for (const [name, artifact] of Object.entries(configuration.artifacts)) {
    if (!name.trim() || !artifact.job.trim() || !artifact.label.trim()) return 'Each named artefact needs a name, a job and a label.';
  }
  return null;
}

type SectionProps = {
  draft: ProjectConfiguration;
  update: (change: (current: ProjectConfiguration) => ProjectConfiguration) => void;
  disabled: boolean;
};

/* ── controls ──────────────────────────────────────────────────────────────── */

function Panel({ title, note, children }: { title: string; note?: ReactNode; children: ReactNode }) {
  return (
    <section className="sheet-panel" data-configuration-panel={title}>
      <header className="panel-head">
        <Kicker>{title}</Kicker>
        {note ? <span className="cfg-help" style={{ marginLeft: 'auto' }}>{note}</span> : null}
      </header>
      <div className="sheet-panel-body">
        <div className="cfg-grid">{children}</div>
      </div>
    </section>
  );
}

function Row({ label, help, path, wide = false, children }: { label: string; help?: ReactNode; path?: string; wide?: boolean; children: ReactNode }) {
  return (
    <div className={wide ? 'cfg-field cfg-field-wide' : 'cfg-field'}>
      <div style={{ minWidth: 0 }}>
        <span className="cfg-label">
          {label}
          {path ? <code className="cfg-path mono">{path}</code> : null}
        </span>
        {help ? <p className="cfg-help">{help}</p> : null}
      </div>
      <div className="cfg-control">{children}</div>
    </div>
  );
}

function TextInput({ label, value, onChange, disabled, placeholder, width = 260 }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  placeholder?: string;
  width?: number;
}) {
  return (
    <input
      className="input"
      type="text"
      aria-label={label}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      style={{ maxWidth: width }}
    />
  );
}

function listOf(text: string): string[] {
  return text.split(',').map((entry) => entry.trim()).filter((entry) => entry.length > 0);
}

/**
 * A comma-separated list. Holds its own text so a trailing comma survives while typing, and
 * takes the stored value back whenever that stops matching (a removed row, a reload).
 */
function ListInput({ label, value, onChange, disabled, placeholder }: {
  label: string;
  value: readonly string[];
  onChange: (value: string[]) => void;
  disabled: boolean;
  placeholder?: string;
}) {
  const [text, setText] = useState(value.join(', '));
  const joined = value.join('\u0000');
  useEffect(() => {
    setText((current) => (listOf(current).join('\u0000') === joined ? current : joined.split('\u0000').filter(Boolean).join(', ')));
  }, [joined]);
  return (
    <input
      className="input"
      type="text"
      aria-label={label}
      value={text}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(event) => {
        setText(event.target.value);
        onChange(listOf(event.target.value));
      }}
      style={{ maxWidth: 260 }}
    />
  );
}

/** A whole number; `undefined` when blank and blank is allowed. Invalid text is not sent. */
function NumberInput({ label, value, onChange, disabled, optional = false }: {
  label: string;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  disabled: boolean;
  optional?: boolean;
}) {
  const [text, setText] = useState(value === undefined ? '' : String(value));
  useEffect(() => {
    setText((current) => {
      const raw = current.trim();
      const shown = raw === '' ? undefined : Number(raw);
      return shown === value ? current : value === undefined ? '' : String(value);
    });
  }, [value]);
  return (
    <input
      className="input"
      type="number"
      aria-label={label}
      value={text}
      disabled={disabled}
      placeholder={optional ? 'default' : undefined}
      onChange={(event) => {
        setText(event.target.value);
        const raw = event.target.value.trim();
        if (raw === '' && optional) onChange(undefined);
        else if (raw !== '' && Number.isInteger(Number(raw))) onChange(Number(raw));
      }}
      style={{ maxWidth: 120, textAlign: 'right' }}
    />
  );
}

function Choice<T extends string>({ label, options, value, onChange, disabled, labelFor }: {
  label: string;
  options: readonly T[];
  value: T;
  onChange: (value: T) => void;
  disabled: boolean;
  labelFor?: (option: T) => string;
}) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map((option) => (
        <button key={option} type="button" aria-pressed={value === option} disabled={disabled} onClick={() => onChange(option)}>
          {labelFor ? labelFor(option) : option}
        </button>
      ))}
    </div>
  );
}

function MultiChoice<T extends string>({ label, options, value, onChange, disabled, labelFor }: {
  label: string;
  options: readonly T[];
  value: readonly T[];
  onChange: (value: T[]) => void;
  disabled: boolean;
  labelFor?: (option: T) => string;
}) {
  return (
    <div className="seg" role="group" aria-label={label} style={{ flexWrap: 'wrap' }}>
      {options.map((option) => {
        const on = value.includes(option);
        return (
          <button
            key={option}
            type="button"
            aria-pressed={on}
            disabled={disabled}
            onClick={() => onChange(on ? value.filter((entry) => entry !== option) : options.filter((entry) => entry === option || value.includes(entry)))}
          >
            {labelFor ? labelFor(option) : option}
          </button>
        );
      })}
    </div>
  );
}

function Toggle({ label, value, onChange, disabled }: { label: string; value: boolean; onChange: (value: boolean) => void; disabled: boolean }) {
  return (
    <button
      type="button"
      className="btn btn-sm"
      aria-label={label}
      aria-pressed={value}
      disabled={disabled}
      onClick={() => onChange(!value)}
      style={{ color: value ? 'var(--good-text)' : undefined }}
    >
      {value ? 'on' : 'off'}
    </button>
  );
}

/* ── sections ──────────────────────────────────────────────────────────────── */

function IdentitySection({ draft, update, disabled }: SectionProps) {
  return (
    <Panel title="Project identity">
      <Row label="Ticket prefix" path="project.tracker_project" help="Prefix Nightshift uses when it mints ticket keys. Empty uses the project slug.">
        <TextInput
          label="Ticket prefix"
          value={draft.project.tracker_project}
          disabled={disabled}
          width={120}
          onChange={(tracker_project) => update((current) => ({ ...current, project: { ...current.project, tracker_project } }))}
        />
      </Row>
    </Panel>
  );
}

function ProfileSection({ draft, update, disabled }: SectionProps) {
  const arrangement = PROFILE_ARRANGEMENTS[draft.profile.kind];
  return (
    <Panel title="Profile, capabilities and engine" note="Unset values follow the profile.">
      <Row label="Profile" path="profile.kind" help="Arranges the defaults for phase, capabilities and vocabularies.">
        <Choice
          label="Profile"
          options={PROJECT_PROFILES}
          value={draft.profile.kind}
          disabled={disabled}
          onChange={(kind) => update((current) => ({ ...current, profile: { ...current.profile, kind } }))}
        />
      </Row>
      <Row label="Phase" path="profile.phase" help={`Follows the profile (${arrangement.phase}) unless set.`}>
        <Choice
          label="Phase"
          options={['profile', ...PROJECT_PHASES] as const}
          value={draft.profile.phase ?? 'profile'}
          disabled={disabled}
          labelFor={(option) => (option === 'profile' ? 'follow profile' : option)}
          onChange={(phase) => update((current) => ({ ...current, profile: { ...current.profile, phase: phase === 'profile' ? null : phase } }))}
        />
      </Row>
      {CAPABILITIES.map((capability) => {
        const explicit = draft.capabilities[capability];
        return (
          <Row key={capability} label={`Capability: ${capability.replaceAll('_', ' ')}`} path={`capabilities.${capability}`} help={CAPABILITY_HELP[capability]}>
            <Choice
              label={`Capability ${capability}`}
              options={['profile', 'on', 'off'] as const}
              value={explicit === undefined ? 'profile' : explicit ? 'on' : 'off'}
              disabled={disabled}
              labelFor={(option) => (option === 'profile' ? `follow profile (${arrangement.capabilities[capability] ? 'on' : 'off'})` : option)}
              onChange={(choice) =>
                update((current) => {
                  const capabilities = { ...current.capabilities };
                  if (choice === 'profile') delete capabilities[capability];
                  else capabilities[capability] = choice === 'on';
                  return { ...current, capabilities };
                })
              }
            />
          </Row>
        );
      })}
      <Row label="Engine / toolchain" path="engine.family" help={'Read by agent prompts. "none" means undeclared — agents are not told a fake engine.'}>
        <Choice
          label="Engine family"
          options={ENGINE_FAMILIES}
          value={draft.engine.family}
          disabled={disabled}
          onChange={(family) => update((current) => ({ ...current, engine: { ...current.engine, family } }))}
        />
      </Row>
      <Row label="Engine name" path="engine.name" help="Display name, e.g. Godot, Unity, or Node/Vite.">
        <TextInput label="Engine name" value={draft.engine.name} disabled={disabled} onChange={(name) => update((current) => ({ ...current, engine: { ...current.engine, name } }))} />
      </Row>
      <Row label="Engine version" path="engine.version" help="The version agents should assume. Empty means unknown.">
        <TextInput label="Engine version" value={draft.engine.version} disabled={disabled} width={120} onChange={(version) => update((current) => ({ ...current, engine: { ...current.engine, version } }))} />
      </Row>
      <Row label="Disciplines" path="taxonomy.disciplines" help="Offered when grouping tickets.">
        <Vocabulary
          label="Disciplines"
          options={DISCIPLINES}
          profileValue={arrangement.disciplines}
          value={draft.taxonomy.disciplines}
          disabled={disabled}
          onChange={(disciplines) => update((current) => ({ ...current, taxonomy: { ...current.taxonomy, disciplines } }))}
        />
      </Row>
      <Row label="Work-item types" path="taxonomy.work_item_types" help="Offered for refine and review emphasis.">
        <Vocabulary
          label="Work-item types"
          options={WORK_ITEM_TYPES}
          profileValue={arrangement.workItemTypes}
          value={draft.taxonomy.work_item_types}
          disabled={disabled}
          onChange={(work_item_types) => update((current) => ({ ...current, taxonomy: { ...current.taxonomy, work_item_types } }))}
        />
      </Row>
    </Panel>
  );
}

/** A vocabulary that follows the profile (`null`) until someone picks its entries. */
function Vocabulary<T extends string>({ label, options, profileValue, value, onChange, disabled }: {
  label: string;
  options: readonly T[];
  profileValue: readonly T[];
  value: T[] | null;
  onChange: (value: T[] | null) => void;
  disabled: boolean;
}) {
  return (
    <div style={{ display: 'grid', gap: 'var(--s2)' }}>
      <label className="cfg-help">
        <input type="checkbox" checked={value === null} disabled={disabled} onChange={(event) => onChange(event.target.checked ? null : [...profileValue])} />{' '}
        Follow profile
      </label>
      <MultiChoice label={label} options={options} value={value ?? profileValue} disabled={disabled || value === null} onChange={onChange} />
    </div>
  );
}

function TagsSection({ draft, update, disabled }: SectionProps) {
  return (
    <Panel title="Required machine tags" note="A machine must advertise every tag to take the work.">
      <Row label="Every stage" path="project.required_tags" help="Tags required for all of this project's work.">
        <ListInput
          label="Required tags for every stage"
          value={draft.project.required_tags}
          placeholder="windows, gpu"
          disabled={disabled}
          onChange={(required_tags) => update((current) => ({ ...current, project: { ...current.project, required_tags } }))}
        />
      </Row>
      {STAGES.map((stage) => (
        <Row key={stage} label={`Extra for ${stage}`} path={`requiredTags.${stage}`}>
          <ListInput
            label={`Required tags for ${stage}`}
            value={draft.requiredTags[stage]}
            disabled={disabled}
            onChange={(tags) => update((current) => ({ ...current, requiredTags: { ...current.requiredTags, [stage]: tags } }))}
          />
        </Row>
      ))}
    </Panel>
  );
}

const DECLARED_ENVIRONMENT: EnvironmentSection = {
  build_hosts: [],
  run_hosts: [],
  tools: [],
  shell: '',
  working_dir: '',
  graphics_session: false,
  input_session: false,
  implementer_operations: ['build'],
  reviewer_operations: [],
};

function EnvironmentEditor({ draft, update, disabled }: SectionProps) {
  const environment = draft.environment;
  const set = (change: Partial<EnvironmentSection>) =>
    update((current) => ({ ...current, environment: { ...(current.environment ?? DECLARED_ENVIRONMENT), ...change } }));
  return (
    <Panel title="Environment requirements" note="Machines that cannot meet these are not given the work.">
      <Row label="Declare requirements" path="environment" help="Off means any live machine with the configured harnesses may take this project's work.">
        <Toggle
          label="Declare environment requirements"
          value={environment !== null}
          disabled={disabled}
          onChange={(on) => update((current) => ({ ...current, environment: on ? { ...DECLARED_ENVIRONMENT } : null }))}
        />
      </Row>
      {environment ? (
        <>
          <Row label="Build hosts" path="environment.build_hosts" help="None selected means any host.">
            <MultiChoice label="Build hosts" options={ENVIRONMENT_HOSTS} value={environment.build_hosts} disabled={disabled} onChange={(build_hosts) => set({ build_hosts })} />
          </Row>
          <Row label="Run hosts" path="environment.run_hosts" help="None selected means the build hosts.">
            <MultiChoice label="Run hosts" options={ENVIRONMENT_HOSTS} value={environment.run_hosts} disabled={disabled} onChange={(run_hosts) => set({ run_hosts })} />
          </Row>
          <Row label="Toolchains" path="environment.tools" help="Toolchain ids every build or run needs, e.g. dotnet-sdk, node.">
            <ListInput label="Toolchains" value={environment.tools} disabled={disabled} onChange={(tools) => set({ tools })} />
          </Row>
          <Row label="Shell" path="environment.shell" help="Shell the commands run under.">
            <Choice label="Shell" options={SHELLS} value={(SHELLS as readonly string[]).includes(environment.shell) ? environment.shell as (typeof SHELLS)[number] : ''} disabled={disabled} labelFor={(shell) => shell || 'platform default'} onChange={(shell) => set({ shell })} />
          </Row>
          <Row label="Android device" path="environment.android_device" help="For runs on a device: any authorised attached device, or one device's digest. Empty means no device.">
            <TextInput
              label="Android device"
              value={environment.android_device ?? ''}
              placeholder="any"
              disabled={disabled}
              onChange={(value) =>
                update((current) => {
                  const next: EnvironmentSection = { ...(current.environment ?? DECLARED_ENVIRONMENT) };
                  if (value.trim()) next.android_device = value.trim();
                  else delete next.android_device;
                  return { ...current, environment: next };
                })
              }
            />
          </Row>
          <Row label="Working directory" path="environment.working_dir" help="Relative to the repository root. Empty means the root.">
            <TextInput label="Working directory" value={environment.working_dir} disabled={disabled} onChange={(working_dir) => set({ working_dir })} />
          </Row>
          <Row label="Graphics session" path="environment.graphics_session" help="Running the project needs a window or GPU.">
            <Toggle label="Graphics session" value={environment.graphics_session} disabled={disabled} onChange={(graphics_session) => set({ graphics_session })} />
          </Row>
          <Row label="Input session" path="environment.input_session" help="Running the project needs synthetic input into that window.">
            <Toggle label="Input session" value={environment.input_session} disabled={disabled} onChange={(input_session) => set({ input_session })} />
          </Row>
          <Row label="Implementer operations" path="environment.implementer_operations" help="What the implementer does locally.">
            <MultiChoice label="Implementer operations" options={ENVIRONMENT_OPERATIONS} value={environment.implementer_operations} disabled={disabled} onChange={(implementer_operations) => set({ implementer_operations })} />
          </Row>
          <Row label="Reviewer operations" path="environment.reviewer_operations" help="What the reviewer does locally. None means a diff-and-CI review that runs anywhere.">
            <MultiChoice label="Reviewer operations" options={ENVIRONMENT_OPERATIONS} value={environment.reviewer_operations} disabled={disabled} onChange={(reviewer_operations) => set({ reviewer_operations })} />
          </Row>
        </>
      ) : null}
    </Panel>
  );
}

function CommandsSection({ draft, update, disabled }: SectionProps) {
  return (
    <Panel title="Commands" note="GitLab CI remains the verification gate; these do not rewrite .gitlab-ci.yml.">
      <Row label="Build command" path="commands.build" help="Given to agents as the project's build command.">
        <TextInput label="Build command" value={draft.commands.build} disabled={disabled} onChange={(build) => update((current) => ({ ...current, commands: { ...current.commands, build } }))} />
      </Row>
      <Row label="Test command" path="commands.test" help="Given to agents beside the build command.">
        <TextInput label="Test command" value={draft.commands.test} disabled={disabled} onChange={(test) => update((current) => ({ ...current, commands: { ...current.commands, test } }))} />
      </Row>
    </Panel>
  );
}

const FINDINGS_SHOWN_LABEL: Record<ReviewFindingsShown, string> = {
  errors: 'Blockers only',
  warnings: 'Blockers and warnings',
  all: 'Everything',
};

function ReviewSection({ draft, update, disabled, defaults }: SectionProps & { defaults: ProjectConfiguration }) {
  const setReview = (change: Partial<ProjectConfiguration['review']>) => update((current) => ({ ...current, review: { ...current.review, ...change } }));
  return (
    <Panel title="Review and merge" note="Auto-merge approves by policy only when every condition holds; anything else waits for a person.">
      <Row label="Auto-merge low-risk changes" path="review.auto_merge" help="Off by default. A ticket whose first review wave passes may be approved by policy.">
        <Toggle label="Auto-merge low-risk changes" value={draft.review.auto_merge} disabled={disabled} onChange={(auto_merge) => setReview({ auto_merge })} />
      </Row>
      <Row label="Auto-merge: largest diff" path="review.auto_merge_max_changed_lines" help={`Added plus removed lines at the reviewed head. Default ${defaults.review.auto_merge_max_changed_lines}.`}>
        <NumberInput
          label="Auto-merge largest diff"
          value={draft.review.auto_merge_max_changed_lines}
          disabled={disabled}
          onChange={(value) => setReview({ auto_merge_max_changed_lines: value ?? draft.review.auto_merge_max_changed_lines })}
        />
      </Row>
      <Row label="Auto-merge: categories" path="review.auto_merge_categories" help={`Ticket categories policy may approve. Default ${defaults.review.auto_merge_categories.join(', ')}.`}>
        <MultiChoice label="Auto-merge categories" options={WORK_CATEGORIES} value={draft.review.auto_merge_categories} disabled={disabled} onChange={(auto_merge_categories) => setReview({ auto_merge_categories })} />
      </Row>
      <Row label="Review cycle cap" path="review.max_cycles" help={`Shown on the board as the review cycle limit. Default ${defaults.review.max_cycles}.`}>
        <NumberInput
          label="Review cycle cap"
          value={draft.review.max_cycles}
          disabled={disabled}
          onChange={(value) => setReview({ max_cycles: value ?? draft.review.max_cycles })}
        />
      </Row>
      <Row
        label="Reviewer findings shown"
        path="review.findings_shown"
        help="Only blockers can fail a review. Warnings and notes never stop a ticket; this hides the ones you do not want to read. Hidden findings are counted on the ticket."
      >
        <Choice
          label="Reviewer findings shown"
          options={REVIEW_FINDINGS_SHOWN}
          value={draft.review.findings_shown}
          disabled={disabled}
          labelFor={(option: ReviewFindingsShown) => FINDINGS_SHOWN_LABEL[option]}
          onChange={(findings_shown) => setReview({ findings_shown })}
        />
      </Row>
    </Panel>
  );
}

const OBSOLETE_POLICY_LABELS: Record<(typeof OBSOLETE_TICKET_POLICIES)[number], string> = {
  propose: 'Ask a person',
  auto_cancel: 'Auto-cancel',
};

function RefineSection({ draft, update, disabled }: SectionProps) {
  return (
    <Panel title="Obsolete tickets" note="When the refiner finds a ticket's work already done, or the code it targets gone, it says so with evidence.">
      <Row
        label="Obsolete tickets"
        path="refine.obsolete_tickets"
        help="Asking a person is the default. Auto-cancel applies only to a high-confidence claim on a ticket with no merge request and nothing unfinished depending on it, after a model from another vendor checks it and agrees. Everyone is notified, and Restore brings the ticket back."
      >
        <Choice
          label="Obsolete tickets"
          options={OBSOLETE_TICKET_POLICIES}
          value={draft.refine.obsolete_tickets}
          disabled={disabled}
          labelFor={(option) => OBSOLETE_POLICY_LABELS[option]}
          onChange={(obsolete_tickets) => update((current) => ({ ...current, refine: { ...current.refine, obsolete_tickets } }))}
        />
      </Row>
    </Panel>
  );
}

function GuidanceSection({ draft, update, disabled }: SectionProps) {
  return (
    <Panel title="Stage guidance" note="Added to the stage prompt as project direction; it cannot change the stage contract.">
      {GUIDANCE_STAGES.map((stage) => (
        <Row key={stage} label={`${stage[0]!.toUpperCase()}${stage.slice(1)} guidance`} path={`guidance.${stage}`} wide>
          <textarea
            className="input"
            aria-label={`${stage} guidance`}
            value={draft.guidance[stage]}
            disabled={disabled}
            rows={3}
            onChange={(event) => update((current) => ({ ...current, guidance: { ...current.guidance, [stage]: event.target.value } }))}
            style={{ width: '100%', resize: 'vertical' }}
          />
        </Row>
      ))}
    </Panel>
  );
}

/** Producers as ordered rows, so a name can be edited without the row jumping. */
type ProducerRow = { name: string } & VerificationProducer;

function producerRowsOf(verification: VerificationSection | null): ProducerRow[] {
  return Object.entries(verification?.producers ?? {}).map(([name, producer]) => ({ name, ...producer }));
}

/** A section with neither producers nor baselines declares nothing, so it is stored as null. */
function verificationFrom(rows: readonly ProducerRow[], rest: Omit<VerificationSection, 'producers'>): VerificationSection | null {
  const producers: Record<string, VerificationProducer> = {};
  for (const { name, ...producer } of rows) producers[name] = producer;
  if (rows.length === 0 && !rest.baselines) return null;
  return { producers, ...(rest.retry ? { retry: rest.retry } : {}), ...(rest.baselines ? { baselines: rest.baselines } : {}) };
}

function VerificationEditor({ draft, update, disabled }: SectionProps) {
  const verification = draft.verification;
  const rows = producerRowsOf(verification);
  const rest = { retry: verification?.retry, baselines: verification?.baselines };
  const digest = useMemo(
    () => (verification && Object.keys(verification.producers).length > 0 ? producerConfigDigest(verification) : ''),
    [verification],
  );
  const setRows = (next: ProducerRow[]) => update((current) => ({ ...current, verification: verificationFrom(next, { retry: current.verification?.retry, baselines: current.verification?.baselines }) }));
  const setRow = (index: number, change: Partial<ProducerRow>) => setRows(rows.map((row, i) => (i === index ? { ...row, ...change } : row)));
  const setRest = (change: Partial<Omit<VerificationSection, 'producers'>>) =>
    update((current) => ({ ...current, verification: verificationFrom(producerRowsOf(current.verification), { retry: current.verification?.retry, baselines: current.verification?.baselines, ...change }) }));
  const baselines = rest.baselines;

  return (
    <section className="sheet-panel" data-configuration-panel="Verification producers">
      <header className="panel-head"><Kicker>Verification producers</Kicker></header>
      <div className="sheet-panel-body">
        {rows.length === 0 ? (
          <p className="cfg-help">
            No producers are declared, so this project’s launch and capture evidence is matched by CI job name. Add a
            producer to have proof read from a job’s result file instead.
          </p>
        ) : (
          <p className="cfg-help">
            Each attempt pins this plan, so an edit here applies to the next attempt rather than to one already running.
            A result must carry the pinned digest to count. Producer plan digest <span className="mono" data-producer-digest>{digest}</span>
          </p>
        )}
        {rows.map((row, index) => (
          <div className="cfg-grid" key={index} data-producer-row={row.name} style={{ marginTop: 'var(--s3)' }}>
            <Row label="Producer name">
              <TextInput label={`Producer ${index + 1} name`} value={row.name} disabled={disabled} width={160} onChange={(name) => setRow(index, { name })} />
              <button type="button" className="btn btn-sm" disabled={disabled} onClick={() => setRows(rows.filter((_, i) => i !== index))}>Remove</button>
            </Row>
            <Row label="CI job" help="Exact GitLab job name.">
              <TextInput label={`Producer ${index + 1} job`} value={row.job} disabled={disabled} onChange={(job) => setRow(index, { job })} />
            </Row>
            <Row label="Provides" help="Requirement names this job proves, e.g. launch, capture.">
              <ListInput label={`Producer ${index + 1} provides`} value={row.provides} disabled={disabled} onChange={(provides) => setRow(index, { provides })} />
            </Row>
            <Row label="Scenarios" help="Only these scenarios of what it provides. Empty means all of them.">
              <ListInput
                label={`Producer ${index + 1} scenarios`}
                value={row.scenarios ?? []}
                disabled={disabled}
                onChange={(scenarios) =>
                  setRows(rows.map((entry, i) => {
                    if (i !== index) return entry;
                    const next: ProducerRow = { name: entry.name, job: entry.job, provides: entry.provides, result: entry.result };
                    return scenarios.length > 0 ? { ...next, scenarios } : next;
                  }))
                }
              />
            </Row>
            <Row label="Result file" help="Repository-relative path of the JSON result the job uploads.">
              <TextInput label={`Producer ${index + 1} result`} value={row.result} disabled={disabled} onChange={(result) => setRow(index, { result })} />
            </Row>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-sm"
          disabled={disabled}
          style={{ marginTop: 'var(--s3)' }}
          onClick={() => setRows([...rows, { name: `producer-${rows.length + 1}`, job: '', provides: [], result: '' }])}
        >
          + Add producer
        </button>

        <div className="cfg-grid" style={{ marginTop: 'var(--s4)' }}>
          <Row label="Job retries" path="verification.retry.max_job_retries" help="Automatic retries of a blocked required job, per head. Blank uses the default.">
            <NumberInput label="Job retries" optional value={rest.retry?.max_job_retries} disabled={disabled || rows.length === 0} onChange={(max_job_retries) => setRest({ retry: retryWith(rest.retry, { max_job_retries }) })} />
          </Row>
          <Row label="Result read attempts" path="verification.retry.read_attempts" help="Failed reads of a result before giving up. Blank uses the default.">
            <NumberInput label="Result read attempts" optional value={rest.retry?.read_attempts} disabled={disabled || rows.length === 0} onChange={(read_attempts) => setRest({ retry: retryWith(rest.retry, { read_attempts }) })} />
          </Row>
          <Row label="Pending wait (ms)" path="verification.retry.pending_wait_ms" help="How long a required job may wait before it parks. Blank uses the default.">
            <NumberInput label="Pending wait" optional value={rest.retry?.pending_wait_ms} disabled={disabled || rows.length === 0} onChange={(pending_wait_ms) => setRest({ retry: retryWith(rest.retry, { pending_wait_ms }) })} />
          </Row>
          <Row label="Baseline directory" path="verification.baselines.dir" help="Where approved baseline images are published. Empty means baseline updates are not offered.">
            <TextInput
              label="Baseline directory"
              value={baselines?.dir ?? ''}
              disabled={disabled}
              onChange={(dir) => setRest({ baselines: dir.trim() ? { ...baselines, dir } : undefined })}
            />
          </Row>
          {baselines ? (
            <>
              <Row label="Baseline file pattern" path="verification.baselines.file" help="Must contain {scenario}. Empty means {scenario}.png.">
                <TextInput label="Baseline file pattern" value={baselines.file ?? ''} disabled={disabled} onChange={(file) => setRest({ baselines: withOptional(baselines, 'file', file) })} />
              </Row>
              <Row label="Actual image marker" path="verification.baselines.actual_marker" help="Empty means actual.">
                <TextInput label="Actual image marker" value={baselines.actual_marker ?? ''} disabled={disabled} width={120} onChange={(value) => setRest({ baselines: withOptional(baselines, 'actual_marker', value) })} />
              </Row>
              <Row label="Difference image marker" path="verification.baselines.diff_marker" help="Empty means diff.">
                <TextInput label="Difference image marker" value={baselines.diff_marker ?? ''} disabled={disabled} width={120} onChange={(value) => setRest({ baselines: withOptional(baselines, 'diff_marker', value) })} />
              </Row>
              <Row label="Require approval" path="verification.baselines.require_approval" help="Refuse to merge a branch that changes a baseline without a recorded approval.">
                <Toggle
                  label="Require baseline approval"
                  value={baselines.require_approval !== false}
                  disabled={disabled}
                  onChange={(on) => setRest({ baselines: { ...baselines, require_approval: on } })}
                />
              </Row>
            </>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function retryWith(current: VerificationSection['retry'], change: NonNullable<VerificationSection['retry']>): VerificationSection['retry'] {
  const next = { ...current, ...change };
  for (const key of Object.keys(next) as Array<keyof typeof next>) if (next[key] === undefined) delete next[key];
  return Object.keys(next).length > 0 ? next : undefined;
}

function withOptional<T extends object, K extends keyof T>(section: T, key: K, value: string): T {
  const next = { ...section };
  if (value.trim()) (next as Record<K, unknown>)[key] = value;
  else delete next[key];
  return next;
}

type ArtifactRow = { name: string; job: string; path: string; label: string };

function ArtifactsEditor({ draft, update, disabled }: SectionProps) {
  const rows: ArtifactRow[] = Object.entries(draft.artifacts).map(([name, artifact]) => ({ name, job: artifact.job, path: artifact.path ?? '', label: artifact.label }));
  const setRows = (next: ArtifactRow[]) => {
    const artifacts: ArtifactsSection = {};
    for (const row of next) artifacts[row.name] = { job: row.job, ...(row.path.trim() ? { path: row.path } : {}), label: row.label };
    update((current) => ({ ...current, artifacts }));
  };
  const setRow = (index: number, change: Partial<ArtifactRow>) => setRows(rows.map((row, i) => (i === index ? { ...row, ...change } : row)));
  return (
    <section className="sheet-panel" data-configuration-panel="Named artefacts">
      <header className="panel-head">
        <Kicker>Named artefacts</Kicker>
        <span className="cfg-help" style={{ marginLeft: 'auto' }}>The ticket card links these first; every other CI upload stays under “more”.</span>
      </header>
      <div className="sheet-panel-body">
        {rows.length === 0 ? <p className="cfg-help">None declared: the card offers every job archive and singles nothing out.</p> : null}
        {rows.map((row, index) => (
          <div className="cfg-grid" key={index} style={{ marginTop: 'var(--s3)' }}>
            <Row label="Name">
              <TextInput label={`Artefact ${index + 1} name`} value={row.name} disabled={disabled} width={160} onChange={(name) => setRow(index, { name })} />
              <button type="button" className="btn btn-sm" disabled={disabled} onClick={() => setRows(rows.filter((_, i) => i !== index))}>Remove</button>
            </Row>
            <Row label="CI job">
              <TextInput label={`Artefact ${index + 1} job`} value={row.job} disabled={disabled} onChange={(job) => setRow(index, { job })} />
            </Row>
            <Row label="Path in upload" help="Empty links the job’s whole archive.">
              <TextInput label={`Artefact ${index + 1} path`} value={row.path} disabled={disabled} onChange={(path) => setRow(index, { path })} />
            </Row>
            <Row label="Label">
              <TextInput label={`Artefact ${index + 1} label`} value={row.label} disabled={disabled} onChange={(label) => setRow(index, { label })} />
            </Row>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-sm"
          disabled={disabled}
          style={{ marginTop: 'var(--s3)' }}
          onClick={() => setRows([...rows, { name: `artefact-${rows.length + 1}`, job: '', path: '', label: '' }])}
        >
          + Add artefact
        </button>
      </div>
    </section>
  );
}

/**
 * The revision line and the autosave explanation. The explanation sits in its own static
 * paragraph and the changing line is held to one line, so the form below does not move while
 * a change waits, saves and lands.
 */
export function ConfigurationStatus({ revision, savedBy, conflict, writable, status }: {
  revision: number;
  savedBy: { at: number | null; by: string | null };
  conflict: boolean;
  writable: boolean;
  status: AutosaveState;
}) {
  const editable = writable && !conflict;
  return (
    <div className="cfg-provenance">
      {editable ? (
        <p className="sheet-note" data-configuration-explanation>
          Changes save automatically and apply to the next attempt; attempts already running keep what they started with.
        </p>
      ) : null}
      <p className={`sheet-note${editable ? ' cfg-provenance-line' : ''}`} data-configuration-state>
        {`Revision ${revision}`}
        {savedBy.at ? ` · saved ${new Date(savedBy.at).toLocaleString()}${savedBy.by ? ` by ${savedBy.by}` : ''}` : ' · built-in defaults, never saved'}
        {' · '}
        {conflict
          ? 'Not saving: reload to continue.'
          : !writable
            ? 'Read only — admin access is required to change project configuration.'
            : status === 'saving'
              ? 'Saving…'
              : status === 'pending'
                ? 'Waiting to save…'
                : status === 'failed' || status === 'invalid'
                  ? 'Not saved.'
                  : 'All changes saved.'}
      </p>
    </div>
  );
}
